import { createBrowserRouter, Navigate } from 'react-router';
import { ROUTES } from '@/constants';
import { RootLayout } from './root';
import { RequireAnonymous, RequireAuth } from './guard';
import { RouteError } from './error';
import { SecurityPage } from './security/page';
import { LoginPage } from './login/page';
import { ResetPasswordPage } from './reset-password/page';
import { DashboardPage } from './dashboard/page';
import { IssuesPage } from './issues/page';
import { ManualPage } from './manual/page';
import { FaqPage } from './faq/page';
import { ProfilePage } from './profile/page';
import { ReviewLinksPage } from './review-links/page';
import { NotificationsPage } from './notifications/page';

/**
 * The route tree.
 *
 * Guards are layout routes rather than per-page checks: a new page added under
 * RequireAuth is protected because of where it sits, not because someone
 * remembered to wrap it. Same reasoning as the server composing its auth chain
 * once per surface.
 *
 * Every link in the header has a route here. `__tests__/routes.test.ts` asserts
 * the two lists match - a nav link with no route silently renders the 404 page,
 * and nothing else catches it because both sides reference a valid constant.
 */
export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [
          { path: ROUTES.login, element: <LoginPage /> },
          { path: ROUTES.resetPassword, element: <ResetPasswordPage /> },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RootLayout />,
            children: [
              { path: ROUTES.home, element: <Navigate to={ROUTES.dashboard} replace /> },
              { path: ROUTES.dashboard, element: <DashboardPage /> },
              { path: ROUTES.reviewLinks, element: <ReviewLinksPage /> },
              { path: ROUTES.notifications, element: <NotificationsPage /> },
              { path: ROUTES.issues, element: <IssuesPage /> },
              { path: ROUTES.profile, element: <ProfilePage /> },

              /*
               * Folded into the pages that now carry their content, and kept as
               * redirects rather than deleted. Somebody has these bookmarked -
               * they were reachable in UAT - and a bookmark that 404s is worse
               * than one that lands on the screen the content moved to.
               */
              {
                path: ROUTES.licences,
                element: <Navigate to={ROUTES.dashboard} replace />,
              },
              {
                path: ROUTES.downloads,
                element: <Navigate to={ROUTES.dashboard} replace />,
              },
              { path: ROUTES.team, element: <Navigate to={ROUTES.profile} replace /> },
              { path: ROUTES.security, element: <SecurityPage /> },
              { path: ROUTES.manual, element: <ManualPage /> },
              { path: ROUTES.faq, element: <FaqPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <RouteError /> },
    ],
  },
]);
